import { toGenericPriceKey, toOracleKey } from '../assets'
import { GenericCurrency, LenderData, PoolData } from '../types'

export interface ExposureInfo {
  asset: GenericCurrency
  collateralFactor: number
}

// ---- flattened type
export type PoolWithMeta = PoolData & {
  chainId: string
  lender: string
  utilitzation: number
  apr: number
  exposure: ExposureInfo[]
  /** current price (if found) */
  price?: number
  /** past price (if found) */
  histPrice?: number
}

type PriceMap = Record<string, number>

function resolvePrice(
  map: PriceMap | undefined,
  key: string | undefined,
  fallbackKey?: string,
): number | undefined {
  if (!map) return undefined
  if (key != null && map[key] != null) return map[key]
  if (fallbackKey != null && map[fallbackKey] != null) return map[fallbackKey]
  return undefined
}

function getPriceKey(asset: GenericCurrency): [string, string] {
  const assetGroup = asset?.assetGroup
  return [
    assetGroup,
    toOracleKey(assetGroup) ??
      toGenericPriceKey(asset?.address, asset?.chainId),
  ]
}

/**
 * Flattens LenderData and enriches each pool with exposure information.
 */
export const generateLendingPools = (
  lenderData: LenderData,
  prices: PriceMap,
  histPrices: PriceMap,
): PoolWithMeta[] => {
  // 1) First, reuse the earlier logic to flatten and group by market
  const flat: (PoolData & { chainId: string; lender: string })[] = []

  for (const [chainId, chain] of Object.entries(lenderData)) {
    for (const [lender, lenderEntry] of Object.entries(chain.data)) {
      for (const pool of Object.values(lenderEntry.data)) {
        flat.push({ ...pool, chainId, lender })
      }
    }
  }

  const byMarket = new Map<string, PoolWithMeta[]>()
  for (const pool of flat) {
    const key = `${pool.chainId}:${pool.lender}`
    if (!byMarket.has(key)) byMarket.set(key, [])
    byMarket.get(key)!.push({
      ...pool,
      apr: pool.depositRate + (pool.intrinsicYield ?? 0),
      utilitzation:
        (Number(pool.totalDebt) + Number(pool.totalDebtStable ?? 0)) /
        Number(pool.totalDeposits),
      exposure: [],
    })
  }

  // 2) Compute exposures within each (chainId,lender) market
  for (const group of byMarket.values()) {
    for (const collateralPool of group) {
      // all configs for the collateral asset where it's allowed as collateral
      const collateralConfigs = Object.values(
        collateralPool.config ?? {},
      ).filter((cfg) => cfg && cfg.collateralDisabled !== true)
      if (!collateralConfigs.length) continue

      const exposuresByPoolId = new Map<string, ExposureInfo>()

      for (const borrowPool of group) {
        // don't expose a pool to itself
        if (borrowPool.poolId === collateralPool.poolId) continue

        for (const collateralCfg of collateralConfigs) {
          const cat = collateralCfg.category
          const borrowCfg = borrowPool.config?.[cat]
          if (!borrowCfg || borrowCfg.debtDisabled === true) continue

          const candidate = borrowCfg.collateralFactor

          const existing = exposuresByPoolId.get(borrowPool.poolId)
          if (!existing || candidate > existing.collateralFactor) {
            exposuresByPoolId.set(borrowPool.poolId, {
              asset: borrowPool.asset,
              collateralFactor: candidate,
            })
          }
        }
      }

      collateralPool.exposure = Array.from(exposuresByPoolId.values())
    }
  }

  // 3) Attach price + histPrice for each pool using getPriceKey(asset)
  for (const group of byMarket.values()) {
    for (const pool of group) {
      const [assetGroup, key] = getPriceKey(pool.asset) ?? [
        undefined,
        undefined,
      ]
      pool.price = resolvePrice(prices, key, assetGroup)
      pool.histPrice = resolvePrice(histPrices, key, assetGroup)
    }
  }

  return Array.from(byMarket.values()).flat()
}
